import EventEmitter from 'eventemitter3';
import styles from './styles.module.scss';
import type { Token } from '../../core/token';
import { deepMerge } from '../../util/data';
import { Option as StandardChartOption, StandardChartIns } from '../../helper/standardChart';

interface DataZoomEvent {
  start: number;
  end: number;
}

interface EChartsInstance {
  on: (eventName: string, callback: (event: DataZoomEvent) => void) => void;
  getModel: () => {
    getComponent: (name: string) => {
      getValueRange: (axis: string, index: number) => number[];
    };
  };
}

interface ThsDataVStandardChart {
  init: (dom: HTMLElement) => StandardChartIns;
}

interface WindowWithThsDataVStandardChart extends Window {
  ThsDataVStandardChart: ThsDataVStandardChart;
}

type Config = {
  data: string[];
  token?: Partial<Token>;
  style?: Partial<StandardChartOption['dataZoom'][0]>;
};

type EventTypes = {
  valueRange: (valueRange: number[]) => void;
  dataZoom: (startIndex: number, endIndex: number) => void;
};

export const defaultToken = {
  type: 'slider',
  show: true,
  bottom: 0,
  height: 16
};

export default class CustomDataZoom extends EventEmitter<EventTypes> {
  private __dom!: HTMLElement;

  private __config: Config;

  private _option!: StandardChartOption;

  chartIns!: StandardChartIns;

  constructor(dom: HTMLElement, config: Config) {
    super();

    this.__dom = dom;
    this.__dom.classList.add(styles.wrap);
    this.__config = config;

    this.__initialize();
  }

  private __initialize() {
    this.__buildDom();
    this.__buildDataZoom();
    this.__event();
  }

  private __buildDom() {
    const $chart = document.createElement('div');
    $chart.classList.add(styles.chart);
    this.__dom.appendChild($chart);
  }

  private __setDomHeight(height: number) {
    this.__dom.style.height = `${height}px`;
  }

  private __buildDataZoom() {
    const xAxisData = this.__config.data;
    const { token, style = {} } = this.__config;
    const dataZoomData = Array(xAxisData.length).fill(0);
    const chartIns = (
      window as unknown as WindowWithThsDataVStandardChart
    ).ThsDataVStandardChart.init(this.__dom.firstChild as HTMLElement);
    // 优先级：用户配置 >> 默认配置 >> 主题配置
    const dataZoomToken = deepMerge(
      deepMerge(token?.dvHXKLine?.dataZoom ?? {}, defaultToken, {
        override: true
      }),
      style,
      {
        override: true
      }
    );
    this.__setDomHeight(dataZoomToken.height as number);

    const option: StandardChartOption = {
      dataZoom: [dataZoomToken],
      tooltip: {},
      axisPointer: {},
      xAxis: {
        __dataIndex: 0,
        __encodeField: 'x',
        type: 'category',
        data: xAxisData,
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: { show: false },
        splitLine: { show: false }
      },
      yAxis: {
        __dataIndex: 0,
        __encodeField: ['y'],
        type: 'value',
        data: [],
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: { show: false },
        splitLine: { show: false }
      },
      series: [
        {
          type: 'bar',
          __encodeField: 'y',
          name: 'data',
          data: dataZoomData
        }
      ]
    };
    this._option = option;
    chartIns.play({ option });

    this.chartIns = chartIns;
  }

  private __event() {
    const self = this;
    const { chartIns } = self;
    const xAxisData = self.__config.data;
    let [dataZoomStartIndex, dataZoomEndIndex] = [0, xAxisData.length - 1];

    const echarts = chartIns.getECharts() as EChartsInstance;
    if (echarts) {
      echarts.on('dv:afterupdate', () => {
        const valueRange = echarts.getModel().getComponent('dataZoom').getValueRange('x', 0);
        self.emit('valueRange', valueRange);
      });

      echarts.on('datazoom', (event: DataZoomEvent) => {
        const startIndex = Math.round((event.start / 100) * (xAxisData.length - 1));
        const endIndex = Math.round((event.end / 100) * (xAxisData.length - 1));

        if (startIndex !== dataZoomStartIndex || endIndex !== dataZoomEndIndex) {
          // 设置行情图的区间
          [dataZoomStartIndex, dataZoomEndIndex] = [startIndex, endIndex];

          self.emit('dataZoom', startIndex, endIndex);
        }
      });
    }
  }

  update(data: string[]) {
    const option = this._option;
    this.__config.data = data;
    if (option.dataZoom?.[0]) {
      option.dataZoom[0].endValue = data.length - 1;
    }
    if (option.xAxis) {
      option.xAxis.data = data;
    }
    if (Array.isArray(option.series) && option.series[0]) {
      option.series[0].data = Array(data.length).fill(0);
    }
    this.chartIns.play({ option });
    this.__event();
  }

  destroy() {
    this.chartIns.destroy();
    this.__dom.innerHTML = '';
  }
}
